import { Link } from '@tanstack/react-router';
import { Gavel } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Order, OrderPayment } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { cardSummary } from '../schemas/order-rules';

const STATUS_STYLES: Record<OrderPayment['status'], string> = {
  pending: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  requires_action: 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25',
  authorized: 'bg-primary/10 text-foreground border-transparent',
  captured: 'bg-success/15 text-success border-transparent dark:bg-success/20',
  failed: 'bg-destructive/10 text-destructive border-transparent',
  cancelled: 'border-border text-muted-foreground',
};

function Payment({ payment }: { payment: OrderPayment }) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const method = payment.methodSnapshot;
  const card = cardSummary(payment.card);
  const failure = payment.failure?.message ?? payment.failure?.code ?? null;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            {card ?? method?.name ?? t(`payment.provider.${payment.provider}`)}
          </p>
          {method?.description ? (
            <p className="text-muted-foreground text-xs">{method.description}</p>
          ) : null}
        </div>
        <Badge variant="outline" className={cn(STATUS_STYLES[payment.status])}>
          {t(`payment.status.${payment.status}`)}
        </Badge>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">{t('payment.amount')}</dt>
        <dd className="text-right tabular-nums">{format.money(payment.amount)}</dd>
        <dt className="text-muted-foreground">{t('payment.captured')}</dt>
        <dd className="text-right tabular-nums">{format.money(payment.amountCaptured)}</dd>
        {payment.amountRefunded.amount > 0 ? (
          <>
            <dt className="text-muted-foreground">{t('payment.refunded')}</dt>
            <dd className="text-right tabular-nums">{format.money(payment.amountRefunded)}</dd>
          </>
        ) : null}
        <dt className="text-muted-foreground">{t('payment.providerLabel')}</dt>
        <dd className="text-right">{t(`payment.providerName.${payment.provider}`)}</dd>
        {payment.provider === 'stripe' ? (
          <>
            <dt className="text-muted-foreground">{t('payment.intent')}</dt>
            <dd
              className="truncate text-right font-mono text-xs"
              title={payment.providerPaymentIntentId ?? undefined}
            >
              {payment.providerPaymentIntentId ?? (
                <span className="text-muted-foreground font-sans">{t('payment.noIntent')}</span>
              )}
            </dd>
            {payment.card?.funding ? (
              <>
                <dt className="text-muted-foreground">{t('payment.funding')}</dt>
                <dd className="text-right">
                  {t(`payment.fundingType.${payment.card.funding}`, {
                    defaultValue: payment.card.funding,
                  })}
                </dd>
              </>
            ) : null}
          </>
        ) : (
          <>
            <dt className="text-muted-foreground">{t('payment.reference')}</dt>
            <dd className="truncate text-right font-mono text-xs">
              {payment.manualReference ?? (
                <span className="text-muted-foreground font-sans">{t('payment.noReference')}</span>
              )}
            </dd>
          </>
        )}
      </dl>
      {failure ? (
        <Alert variant="destructive">
          <AlertDescription>{t('payment.failure', { reason: failure })}</AlertDescription>
        </Alert>
      ) : null}
      {payment.capturedAt ? (
        <p className="text-muted-foreground text-xs" title={format.utc(payment.capturedAt)}>
          {t('payment.capturedAt', { when: format.relative(payment.capturedAt) })}
        </p>
      ) : null}
      {method ? (
        <div className="bg-muted/50 rounded-md border p-2.5">
          <p className="text-muted-foreground mb-1 text-[11px] font-medium tracking-wide uppercase">
            {t('payment.instructions')}
          </p>
          <p className="text-xs whitespace-pre-line">
            {method.instructions ?? t('payment.noInstructions')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** A won auction lot: what the winner bid, the premium on top, and the auction it came from. */
function AuctionSettlement({
  auction,
  storeId,
}: {
  auction: NonNullable<Order['auction']>;
  storeId: string | undefined;
}) {
  const { t } = useTranslation('orders');
  const format = useFormat();
  return (
    <Alert data-testid="auction-settlement">
      <Gavel aria-hidden />
      <AlertTitle>{t('auction.title')}</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <span>{t('auction.markPaidHint')}</span>
        <dl className="text-foreground grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">{t('auction.hammer')}</dt>
          <dd className="text-right tabular-nums">{format.money(auction.hammerPrice)}</dd>
          {auction.buyerPremium.amount > 0 ? (
            <>
              <dt className="text-muted-foreground">{t('auction.premium')}</dt>
              <dd className="text-right tabular-nums">{format.money(auction.buyerPremium)}</dd>
            </>
          ) : null}
        </dl>
        {storeId ? (
          <Link
            to="/s/$storeId/auctions/$auctionId"
            params={{ storeId, auctionId: auction.id }}
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            {t('auction.open')}
          </Link>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Each payment: the card (brand, last4, intent) or the manual method as the shopper saw it, and
 * what has been received and refunded against it. An auction win says how it gets paid: staff
 * mark it paid when the winner pays.
 */
export function OrderPaymentCard({
  payments,
  auction = null,
  storeId,
}: {
  payments: readonly OrderPayment[];
  auction?: Order['auction'];
  storeId?: string;
}) {
  const { t } = useTranslation('orders');
  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('payment.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {auction ? <AuctionSettlement auction={auction} storeId={storeId} /> : null}
        {payments.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('payment.none')}</p>
        ) : (
          payments.map((payment) => <Payment key={payment.id} payment={payment} />)
        )}
      </CardContent>
    </Card>
  );
}
